import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronRight, Sparkles, GraduationCap, Library } from 'lucide-react';
import { getAllSubjects, getAllClasses, getWordMeaningSubjects } from '../firebase/services';

const HomePage = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [wordMeaningSubjects, setWordMeaningSubjects] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [loading, setLoading] = useState(true);
  // CRITICAL: Set class from localStorage IMMEDIATELY to prevent theme flash
  const [selectedClass, setSelectedClass] = useState(localStorage.getItem('selectedClass') || null);
  const [showClassSelection, setShowClassSelection] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]);
  // Cache for all classes data - keeps data for all classes separately
  const [allClassesData, setAllClassesData] = useState({});

  // Fallback theme colors for each class (used when database theme is not available)
  const classThemes = {
    '9th': {
      gradient: 'from-orange-500 to-amber-600',
      light: 'from-orange-50 to-amber-50',
      border: 'border-orange-200',
      hoverBorder: 'hover:border-orange-400',
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      iconBg: 'from-orange-100 to-amber-100',
      iconText: 'text-orange-700',
      shadow: 'hover:shadow-orange-100/50',
      btnHover: 'hover:bg-gradient-to-r hover:from-orange-500 hover:to-amber-600',
      primary: '#f97316',
      secondary: '#ea580c'
    },
    '10th': {
      gradient: 'from-blue-500 to-indigo-600',
      light: 'from-blue-50 to-indigo-50',
      border: 'border-blue-200',
      hoverBorder: 'hover:border-blue-400',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      iconBg: 'from-blue-100 to-indigo-100',
      iconText: 'text-blue-700',
      shadow: 'hover:shadow-blue-100/50',
      btnHover: 'hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-600',
      primary: '#3b82f6',
      secondary: '#4f46e5'
    },
    '11th': {
      gradient: 'from-emerald-500 to-teal-600',
      light: 'from-emerald-50 to-teal-50',
      border: 'border-emerald-200',
      hoverBorder: 'hover:border-emerald-400',
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
      iconBg: 'from-emerald-100 to-teal-100',
      iconText: 'text-emerald-700',
      shadow: 'hover:shadow-emerald-100/50',
      btnHover: 'hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-600',
      primary: '#10b981',
      secondary: '#14b8a6'
    },
    '12th': {
      gradient: 'from-pink-500 to-rose-600',
      light: 'from-pink-50 to-rose-50',
      border: 'border-pink-200',
      hoverBorder: 'hover:border-pink-400',
      bg: 'bg-pink-50',
      text: 'text-pink-600',
      iconBg: 'from-pink-100 to-rose-100',
      iconText: 'text-pink-700',
      shadow: 'hover:shadow-pink-100/50',
      btnHover: 'hover:bg-gradient-to-r hover:from-pink-500 hover:to-rose-600',
      primary: '#ec4899',
      secondary: '#f43f5e'
    }
  };

  // Get dynamic theme from database or fallback to default - OPTIMIZED with useMemo
  const currentTheme = useMemo(() => {
    // Always use fallback theme for consistent colors
    const fallbackTheme = classThemes[selectedClass] || classThemes['11th'];
    
    // If no availableClasses yet, return fallback immediately
    if (!availableClasses || availableClasses.length === 0) {
      return fallbackTheme;
    }
    
    const classData = availableClasses.find(c => c.name === selectedClass);
    
    if (classData && classData.theme) {
      // Use complete theme from database if all properties exist
      if (classData.theme.iconBg && classData.theme.iconText) {
        return {
          ...fallbackTheme,
          ...classData.theme,
          // Ensure critical properties are set
          gradient: classData.theme.gradient || fallbackTheme.gradient,
          light: classData.theme.lightGradient || classData.theme.light || fallbackTheme.light,
          primary: classData.theme.primary || fallbackTheme.primary,
          secondary: classData.theme.secondary || fallbackTheme.secondary
        };
      }
      
      // Partial theme - merge with fallback
      return {
        ...fallbackTheme,
        gradient: classData.theme.gradient || fallbackTheme.gradient,
        light: classData.theme.lightGradient || fallbackTheme.light,
        primary: classData.theme.primary || fallbackTheme.primary,
        secondary: classData.theme.secondary || fallbackTheme.secondary
      };
    }
    
    // Use fallback theme completely
    return fallbackTheme;
  }, [selectedClass, availableClasses]);

  useEffect(() => {
    // Load available classes first - OPTIMIZED
    const initializeApp = async () => {
      // Check if class is already selected in localStorage
      const savedClass = localStorage.getItem('selectedClass');
      
      // Load classes in background
      const classes = await getAllClasses();
      setAvailableClasses(classes);
      
      if (savedClass) {
        // Set class IMMEDIATELY to avoid theme flash
        setSelectedClass(savedClass);
        updateManifestAndTheme(savedClass, classes); // Pass classes directly
        loadSubjects(savedClass);
      } else {
        setShowClassSelection(true);
        setLoading(false);
      }
    };
    
    initializeApp();
  }, []);
  
  const handleClassSelect = (classValue) => {
    setSelectedClass(classValue);
    localStorage.setItem('selectedClass', classValue);
    setShowClassSelection(false);
    setIsClassDropdownOpen(false);
    // NO LOADING - Smooth transition
    loadSubjects(classValue);
    
    // Update manifest and theme color dynamically
    updateManifestAndTheme(classValue);
  };
  
  // Function to update manifest link and theme color - OPTIMIZED
  const updateManifestAndTheme = (classValue, classes = availableClasses) => {
    // Get theme from database
    const classData = classes.find(c => c.name === classValue);
    const themeColor = classData?.theme?.primary || '#10b981';
    
    const manifestFiles = {
      '10th': '/manifest-blue.json',
      '11th': '/manifest-green.json',
      '12th': '/manifest-pink.json',
      '9th': '/manifest-green.json' // fallback
    };
    
    const manifestFile = manifestFiles[classValue] || manifestFiles['11th'];
    
    // Update theme-color meta tag
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
    if (themeMetaTag) {
      themeMetaTag.setAttribute('content', themeColor);
    }
    
    // Update status bar style for iOS
    const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (statusBarMeta) {
      statusBarMeta.setAttribute('content', 'light-content');
    }
    
    // Update manifest link dynamically based on theme
    const manifestLink = document.getElementById('manifest-link');
    if (manifestLink) {
      manifestLink.setAttribute('href', manifestFile);
    }
  };

  const loadSubjects = async (classValue) => {
    // Check if we already have data for this class in memory
    if (allClassesData[classValue]) {
      // Use cached data from state
      setSubjects(allClassesData[classValue].subjects || []);
      setWordMeaningSubjects(allClassesData[classValue].wmSubjects || []);
      setLoading(false);
      return;
    }
    
    // Check sessionStorage cache
    const cacheKey = `subjects_${classValue}`;
    const wmCacheKey = `wm_subjects_${classValue}`;
    const cacheTime = 5 * 60 * 1000; // 5 minutes cache
    
    try {
      const cached = sessionStorage.getItem(cacheKey);
      const wmCached = sessionStorage.getItem(wmCacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_time`);
      
      // Use cache if available and fresh
      if (cached && wmCached && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < cacheTime) {
          const cachedSubjects = JSON.parse(cached);
          const cachedWmSubjects = JSON.parse(wmCached);
          
          setSubjects(cachedSubjects);
          setWordMeaningSubjects(cachedWmSubjects);
          
          // Store in memory cache
          setAllClassesData(prev => ({
            ...prev,
            [classValue]: {
              subjects: cachedSubjects,
              wmSubjects: cachedWmSubjects
            }
          }));
          
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      console.log('Cache read failed, loading fresh data');
    }
    
    setLoading(true);
    
    try {
      // Load from Firebase - ONLY for selected class
      const [data, wmData] = await Promise.all([
        getAllSubjects(),
        getWordMeaningSubjects()
      ]);
      
      // Filter subjects by selected class ONLY
      const filteredSubjects = data.filter(s => s.class === classValue || !s.class);
      setSubjects(filteredSubjects);
      
      // Filter word meaning subjects by selected class ONLY
      const filteredWMSubjects = wmData.filter(s => s.class === classValue || !s.class);
      setWordMeaningSubjects(filteredWMSubjects);
      
      // Store in memory cache - keep all classes data
      setAllClassesData(prev => ({
        ...prev,
        [classValue]: {
          subjects: filteredSubjects,
          wmSubjects: filteredWMSubjects
        }
      }));
      
      // Cache the data in sessionStorage
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(filteredSubjects));
        sessionStorage.setItem(wmCacheKey, JSON.stringify(filteredWMSubjects));
        sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      } catch (e) {
        console.log('Cache write failed');
      }
    } catch (error) {
      console.error('Failed to load from Firebase:', error);
      setSubjects([]);
      setWordMeaningSubjects([]);
    }
    
    setLoading(false);
  };

  const handleSubjectSelect = (subjectId) => {
    navigate(`/chapters/${subjectId}`);
  };

  const handleWordMeaningSubjectSelect = (subjectId) => {
    navigate(`/word-meaning/chapters/${subjectId}`);
  };

  // Class Selection Screen
  if (showClassSelection && !loading) {
    // Don't show popup until classes are loaded
    if (availableClasses.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <svg className="w-20 h-20 animate-spin" viewBox="0 0 100 100">
                <circle
                  className="stroke-current text-emerald-200"
                  strokeWidth="8"
                  fill="none"
                  cx="50"
                  cy="50"
                  r="40"
                />
                <circle
                  className="stroke-current text-emerald-600"
                  strokeWidth="8"
                  strokeLinecap="round"
                  fill="none"
                  cx="50"
                  cy="50"
                  r="40"
                  strokeDasharray="251.2"
                  strokeDashoffset="62.8"
                  style={{ transformOrigin: 'center', transform: 'rotate(-90deg)' }}
                />
              </svg>
            </div>
            <p className="text-gray-600 mt-4 font-medium">Loading classes...</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Shapes */}
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-400/15 rounded-full blur-3xl animate-pulse-slow"></div>
          
          {/* Animated Grid */}
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          
          {/* Sparkles */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full animate-twinkle"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            ></div>
          ))}
        </div>

        <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl p-8 max-w-2xl w-full border border-white/40 relative z-10 animate-scale-up">
          {/* Decorative Corner Elements */}
          <div className="absolute -top-4 -left-4 w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full blur-xl opacity-60 animate-pulse"></div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-xl opacity-60 animate-pulse" style={{ animationDelay: '1s' }}></div>
          
          {/* Header Section */}
          <div className="text-center mb-8 relative">
            {/* Animated Icon */}
            <div className="relative inline-block mb-4 md:mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 rounded-3xl blur-2xl opacity-50 animate-pulse-glow"></div>
              <div className="relative w-20 h-20 md:w-28 md:h-28 bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl transform hover:rotate-12 transition-transform duration-300">
                <GraduationCap className="w-12 h-12 md:w-16 md:h-16 text-white animate-bounce-slow" />
              </div>
              {/* Floating Icons */}
              <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center shadow-lg animate-bounce-pulse border-2 border-yellow-400">
                <span className="text-xl md:text-2xl">⭐</span>
              </div>
              <div className="absolute -bottom-1 -left-1 md:-bottom-2 md:-left-2 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center shadow-lg animate-bounce-pulse border-2 border-blue-400" style={{ animationDelay: '0.5s' }}>
                <span className="text-xl md:text-2xl">🎯</span>
              </div>
            </div>
            
            {/* Title with Gradient */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent mb-2 md:mb-3 animate-slide-down tracking-tight px-4">
              Welcome to QuizMaster! 🎓
            </h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg lg:text-xl animate-fade-in font-medium px-4" style={{ animationDelay: '0.2s' }}>
              Your Journey to Excellence Starts Here
            </p>
            
            {/* Decorative Line */}
            <div className="flex items-center justify-center gap-3 mt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="h-1 w-20 bg-gradient-to-r from-transparent via-purple-500 to-purple-500 rounded-full"></div>
              <span className="text-2xl">✨</span>
              <div className="h-1 w-20 bg-gradient-to-r from-purple-500 via-pink-500 to-transparent rounded-full"></div>
            </div>
          </div>
          
          {/* Classes Grid */}
          <div className="space-y-3 mb-6">
            {availableClasses.map((cls, index) => {
              const classData = availableClasses.find(c => c.name === cls.name);
              const themeColor = classData?.theme?.primary || '#10B981';
              const themeGradient = classData?.theme?.gradient || 'from-emerald-500 to-teal-600';
              
              return (
                <button
                  key={cls.id}
                  onClick={() => handleClassSelect(cls.name)}
                  className="w-full group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 animate-slide-in-left"
                  style={{ 
                    animationDelay: `${index * 0.1 + 0.4}s`,
                    background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`
                  }}
                >
                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                  
                  {/* Content */}
                  <div className="relative p-5 flex items-center justify-between border-2 rounded-2xl transition-all duration-300 group-hover:border-opacity-100"
                    style={{ borderColor: `${themeColor}40` }}>
                    <div className="flex items-center gap-4">
                      {/* Class Icon */}
                      {cls.icon ? (
                        <img 
                          src={cls.icon} 
                          alt={cls.name}
                          className="w-16 h-16 rounded-xl shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300"
                        />
                      ) : (
                        <div 
                          className="w-16 h-16 rounded-xl shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 relative overflow-hidden flex items-center justify-center"
                          style={{ 
                            background: `linear-gradient(135deg, ${themeColor}, ${classData?.theme?.secondary || themeColor})`
                          }}
                        >
                          <BookOpen className="w-8 h-8 text-white" />
                        </div>
                      )}
                      
                      {/* Class Info */}
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 group-hover:text-gray-950 transition-colors">
                          Class {cls.name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 font-medium hidden sm:block">
                          {classData?.theme?.name || 'Default'} Theme • Tap to start
                        </p>
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-125 transition-transform duration-300"
                        style={{ backgroundColor: `${themeColor}20` }}>
                        <ChevronRight 
                          className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" 
                          style={{ color: themeColor }}
                        />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Credit Section */}
          <div className="text-center pt-6 border-t-2 border-gray-100 animate-fade-in" style={{ animationDelay: '1s' }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-sm text-gray-500 font-medium">Made with</span>
              <div className="relative inline-block">
                <span className="text-red-500 text-xl animate-heartbeat">❤️</span>
                <span className="absolute inset-0 text-red-500 text-xl animate-ping opacity-75">❤️</span>
              </div>
              <span className="text-sm text-gray-500 font-medium">by</span>
            </div>
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 blur-lg opacity-30 animate-pulse"></div>
              <h4 className="relative text-3xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent">
                Prince
              </h4>
            </div>
            <p className="text-xs text-gray-400 mt-2 font-medium">
              🚀 Empowering Students • Building Dreams
            </p>
          </div>
        </div>

        <style jsx="true">{`
          @keyframes scale-up {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          
          @keyframes slide-in-left {
            from {
              opacity: 0;
              transform: translateX(-50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes twinkle {
            0%, 100% {
              opacity: 0;
              transform: scale(0);
            }
            50% {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes heartbeat {
            0%, 100% {
              transform: scale(1);
            }
            25% {
              transform: scale(1.3);
            }
            50% {
              transform: scale(1);
            }
            75% {
              transform: scale(1.2);
            }
          }
          
          .bg-grid-pattern {
            background-image: 
              linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
            background-size: 50px 50px;
          }
          
          .animate-scale-up {
            animation: scale-up 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
          
          .animate-slide-in-left {
            animation: slide-in-left 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
            opacity: 0;
          }
          
          .animate-twinkle {
            animation: twinkle 3s ease-in-out infinite;
          }
          
          .animate-heartbeat {
            animation: heartbeat 1.5s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    // Get proper border color based on selected class
    const spinnerBorderClass = selectedClass === '9th' ? 'border-orange-500' :
                                 selectedClass === '10th' ? 'border-blue-500' :
                                 selectedClass === '12th' ? 'border-pink-500' :
                                 'border-emerald-500'; // 11th default
    
    return (
      <div className={`min-h-screen bg-gradient-to-br ${currentTheme.light} flex items-center justify-center`}>
        <div className="text-center">
          <div className={`w-16 h-16 border-4 ${spinnerBorderClass} border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${currentTheme.light}`}>
      {/* Header */}
      <div className={`bg-white/80 backdrop-blur-sm border-b ${currentTheme.border} sticky top-0 z-10`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">QuizMaster</h1>
                <p className="text-xs text-gray-500">Test Your Knowledge</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Class Selector Icon */}
              <div className="relative">
                <button
                  onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${currentTheme.border} ${currentTheme.hoverBorder} hover:${currentTheme.bg} transition-all`}
                  title="Change Class"
                >
                  <GraduationCap className={`w-5 h-5 ${currentTheme.text}`} />
                  <span className="text-sm font-semibold text-gray-700">{selectedClass}</span>
                </button>
                
                {isClassDropdownOpen && (
                  <div className={`absolute right-0 mt-2 w-32 bg-white border-2 ${currentTheme.border} rounded-lg shadow-lg z-50`}>
                    {availableClasses.map((cls) => {
                      // Use database theme if available, otherwise fallback
                      const clsTheme = (cls.theme && cls.theme.iconBg) 
                        ? cls.theme 
                        : (classThemes[cls.name] || classThemes['11th']);
                      
                      return (
                        <button
                          key={cls.id}
                          onClick={() => {
                            handleClassSelect(cls.name);
                            setIsClassDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2 text-left hover:${clsTheme.bg || 'bg-gray-100'} transition-colors ${
                            selectedClass === cls.name ? `${clsTheme.bg || 'bg-gray-100'} font-semibold` : ''
                          }`}
                        >
                          {cls.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8 animate-fade-in">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Select a Subject</h2>
          <p className="text-gray-600">Choose your subject to start practicing</p>
        </div>

        {/* Subject Cards */}
        {subjects.length === 0 ? (
          <div className="text-center py-8 bg-white/50 rounded-2xl border-2 border-dashed border-gray-200 mb-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No subjects available yet</p>
          </div>
        ) : (
          <div className="grid gap-4 mb-12">
            {subjects.map((subject, index) => (
              <div
                key={subject.id}
                className="group cursor-pointer"
                onClick={() => handleSubjectSelect(subject.id)}
                onMouseEnter={() => setHoveredCard(subject.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  animation: `slideUp 0.5s ease-out ${index * 0.1}s both`
                }}
              >
                <div className={`bg-white rounded-2xl p-6 border-2 border-gray-100 ${currentTheme.hoverBorder.replace('hover:', 'hover:border-')} transition-all duration-300 hover:shadow-xl ${currentTheme.shadow} hover:-translate-y-1`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        hoveredCard === subject.id 
                          ? `bg-gradient-to-br ${currentTheme.gradient} shadow-lg scale-110` 
                          : `bg-gradient-to-br ${currentTheme.iconBg}`
                      }`}>
                        <BookOpen className={`w-7 h-7 transition-colors ${
                          hoveredCard === subject.id ? 'text-white' : currentTheme.iconText
                        }`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-1">{subject.name}</h3>
                        <p className="text-sm text-gray-500">{subject.totalChapters || 0} Chapters</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-6 h-6 text-gray-400 transition-all duration-300 ${
                      hoveredCard === subject.id ? `translate-x-2 ${currentTheme.text}` : ''
                    }`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Word Meaning Section */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${currentTheme.gradient} flex items-center justify-center shadow-md`}>
              <Library className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Word Meaning</h2>
              <p className="text-sm text-gray-600">Page-wise vocabulary practice</p>
            </div>
          </div>
        </div>

        {/* Word Meaning Subject Cards */}
        {wordMeaningSubjects.length === 0 ? (
          <div className="text-center py-8 bg-white/50 rounded-2xl border-2 border-dashed border-gray-200">
            <Library className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No word meaning subjects yet</p>
          </div>
        ) : (
          <div className="grid gap-4 mb-20">
            {wordMeaningSubjects.map((subject, index) => (
              <div
                key={`wm-${subject.id}`}
                className="group cursor-pointer"
                onClick={() => handleWordMeaningSubjectSelect(subject.id)}
                onMouseEnter={() => setHoveredCard(`wm-${subject.id}`)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  animation: `slideUp 0.5s ease-out ${(subjects.length + index) * 0.1}s both`
                }}
              >
                <div className={`bg-gradient-to-br from-white to-${currentTheme.bg}/20 rounded-2xl p-6 border-2 ${currentTheme.border} ${currentTheme.hoverBorder} transition-all duration-300 hover:shadow-xl ${currentTheme.shadow} hover:-translate-y-1`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        hoveredCard === `wm-${subject.id}` 
                          ? `bg-gradient-to-br ${currentTheme.gradient} shadow-lg scale-110` 
                          : `bg-gradient-to-br ${currentTheme.iconBg}`
                      }`}>
                        <Library className={`w-7 h-7 transition-colors ${
                          hoveredCard === `wm-${subject.id}` ? 'text-white' : currentTheme.iconText
                        }`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-1">{subject.name}</h3>
                        <p className="text-sm text-gray-500">{subject.totalChapters || 0} Chapters</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-6 h-6 text-gray-400 transition-all duration-300 ${
                      hoveredCard === `wm-${subject.id}` ? `translate-x-2 ${currentTheme.text}` : ''
                    }`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx="true">{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes slide-down-desktop {
          from {
            opacity: 0;
            transform: translateY(-100px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes slide-up-mobile {
          from {
            opacity: 0;
            transform: translateY(100px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes slide-in-stagger {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }
        
        @keyframes float-delayed {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-25px) rotate(-5deg);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.1;
            transform: scale(1);
          }
          50% {
            opacity: 0.2;
            transform: scale(1.05);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }
        
        .animate-slide-down-desktop {
          animation: slide-down-desktop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .animate-slide-up-mobile {
          animation: slide-up-mobile 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .animate-slide-in-stagger {
          animation: slide-in-stagger 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        
        /* Mobile: slide from bottom */
        @media (max-width: 768px) {
          .animate-slide-down-desktop {
            animation: slide-up-mobile 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;